import { useRef, type ComponentPropsWithoutRef } from 'react';
import { Toggletip, ToggletipButton, ToggletipContent } from '@carbon/react';
import { Information } from '@carbon/react/icons';
import { useEscapeClosesPopover } from './hooks';
import styles from './area-geometry-editor.module.scss';

export const REFERENCE_LAYERS_TITLE = 'Reference Area Layers';

/** What reference layers are for, behind the info button by their heading. */
export const REFERENCE_LAYERS_HELP =
  "Reference layers show other areas on the map while you edit this one. Use them as a guide, or pick the Trace tool and drag one of this area's vertices onto a shown boundary to follow it exactly.";

/**
 * Reference Area Layers' accordion heading, with an info button after its title.
 * The button can't sit in the title, which is inside the heading's own button, so
 * it lies over the heading on a hidden copy of the title that sets it just past
 * the words.
 *
 * @param {ComponentPropsWithoutRef<'button'>} props - What the accordion gives its heading.
 * @return {JSX.Element} The heading.
 */
export function ReferenceLayersHeading(
  props: ComponentPropsWithoutRef<'button'>
): JSX.Element {
  const ref = useRef<HTMLSpanElement>(null);
  useEscapeClosesPopover(ref);
  return (
    <div className={styles.refAreasHeading}>
      <button type="button" {...props} />
      <span ref={ref} className={styles.refAreasHelp}>
        <span className="bx--accordion__title" aria-hidden="true">
          {REFERENCE_LAYERS_TITLE}
        </span>
        <Toggletip align="bottom-right" autoAlign className={styles.toolHelp}>
          <ToggletipButton label="About reference area layers">
            <Information />
          </ToggletipButton>
          <ToggletipContent>
            <p>{REFERENCE_LAYERS_HELP}</p>
          </ToggletipContent>
        </Toggletip>
      </span>
    </div>
  );
}
